import { useEffect, useState } from "react";
import { MvnMessage } from "@newLibs/utils";
import { notify } from "@newHooks/context/snackbar/notify";
import { useMyRoleForFolderByIdQuery } from "@newHooks/query/folder/myRoleForFolderById.query";
import { useMyRoleForFolderFileByIdQuery } from "@newHooks/query/folder/myRoleForFolderFileById.query";
import { COMMON_CODE } from "@maven-msg/code/common";
import { DOCS_FOLDER_CODE } from "@maven-msg/code/docs";
import { RestError } from "@maven-rest/common/HttpRequest";
import { GetMyRoleForFolderByIdResponse, GetMyRoleForFolderFileByIdResponse } from "@maven-rest/docs/Folder.dto";
import { FolderSharingRole } from "@maven-rest/docs/FolderSharing.dto";

export function useSharingRole({ folderId, fileId, role }: { folderId?: bigint; fileId?: bigint; role?: FolderSharingRole }) {
  const [isAuthorized, setIsAuthorized] = useState<boolean>();
  const [myRole, setMyRole] = useState<FolderSharingRole | null>(null);
  const [error, setError] = useState<RestError<GetMyRoleForFolderFileByIdResponse | GetMyRoleForFolderByIdResponse>>();

  const { isInitialLoading: isMyRoleForFolderFileByIdInitialLoading } = useMyRoleForFolderFileByIdQuery(
    { fileId: fileId || BigInt(-1) },
    {
      enabled: !!fileId,
      onSuccess: (data) => {
        const myRole = data?.role || null;
        const isAuthorized = ((sharingRoles) => {
          if (!role || !myRole) return undefined;
          return sharingRoles.indexOf(myRole) >= sharingRoles.indexOf(role);
        })(Object.values(FolderSharingRole));

        setIsAuthorized(isAuthorized);
        setMyRole(myRole);
      },
      onError: (error) => {
        if (error.responseCode === DOCS_FOLDER_CODE.DOCS_FOLDER_SHARING_NOT_AUTHORIZED.code) {
          setMyRole((error.response?.data?.binds?.authorizedRole as FolderSharingRole) || null);
          setIsAuthorized(false);
          return;
        }

        setMyRole(null);
        setIsAuthorized(undefined);
        setError(error);
      },
    }
  );

  const { isInitialLoading: isMyRoleForFolderByIdInitialLoading } = useMyRoleForFolderByIdQuery(
    { folderId: folderId || BigInt(-1) },
    {
      enabled: !!folderId && !fileId,
      onSuccess: (data) => {
        const myRole = data?.role || null;
        const isAuthorized = ((sharingRoles) => {
          if (!role || !myRole) return undefined;
          return sharingRoles.indexOf(myRole) >= sharingRoles.indexOf(role);
        })(Object.values(FolderSharingRole));

        setIsAuthorized(isAuthorized);
        setMyRole(myRole);
      },
      onError: (error) => {
        if (error.responseCode === DOCS_FOLDER_CODE.DOCS_FOLDER_SHARING_NOT_AUTHORIZED.code) {
          setMyRole((error.response?.data?.binds?.authorizedRole as FolderSharingRole) || null);
          setIsAuthorized(false);
          return;
        }

        setMyRole(null);
        setIsAuthorized(undefined);
        setError(error);

        notify({ message: MvnMessage(COMMON_CODE.UNHANDLED_EXCEPTION_WITH_MSG, { message: error.responseMessage }), severity: "danger" });
      },
    }
  );

  useEffect(() => {
    setMyRole(null);
    setError(undefined);
    setIsAuthorized(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, fileId, folderId]);

  return { isAuthorized, myRole, error, isLoading: isMyRoleForFolderFileByIdInitialLoading || isMyRoleForFolderByIdInitialLoading };
}
